import { useMemo, useState } from "react";
import { DndContext, useDroppable, type DragEndEvent } from "@dnd-kit/core";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatNOK } from "@/lib/format";
import { OfferBoardCard } from "./OfferBoardCard";
import { LostReasonDialog } from "./LostReasonDialog";
import { SendOfferDialog } from "./SendOfferDialog";
import { missingBeforeSend } from "@/lib/offer-status-patch";
import { useOfferStatusChange } from "@/hooks/use-offer-status-change";
import { useOfferPipelineWeights } from "@/hooks/use-offer-pipeline";
import { bucketFor, type PipelineBucket, type PipelineOffer } from "@/lib/offer-pipeline-calc";
import type { OfferWithCustomer, OfferStatus } from "@/hooks/use-offers";
import { cn } from "@/lib/utils";

interface ColumnDef {
  bucket: PipelineBucket;
  label: string;
  /** Bøtter uten en direkte skrivbar status er ikke gyldige drop-mål (expired er avledet). */
  droppable: boolean;
}

const CORE_COLUMNS: ColumnDef[] = [
  { bucket: "draft", label: "Utkast", droppable: true },
  { bucket: "sent", label: "Sendt", droppable: true },
];

const CLOSED_COLUMNS: ColumnDef[] = [
  { bucket: "expired", label: "Utløpt", droppable: false },
  { bucket: "accepted", label: "Akseptert", droppable: true },
  { bucket: "lost", label: "Tapt", droppable: true },
];

function toPipelineOffer(o: OfferWithCustomer): PipelineOffer {
  return {
    id: o.id,
    status: o.status,
    probability: o.probability,
    valid_until: o.valid_until,
    total_amount: o.total_amount,
    created_by: o.created_by,
    writers: o.writers,
  };
}

function DroppableColumn({
  column,
  offers,
  rawKr,
  weightedKr,
  children,
}: {
  column: ColumnDef;
  offers: OfferWithCustomer[];
  rawKr: number;
  weightedKr: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.bucket, disabled: !column.droppable });
  return (
    <div
      ref={setNodeRef}
      data-testid={`offer-board-column-${column.bucket}`}
      className={cn(
        "flex-1 min-w-[220px] max-w-[280px] flex flex-col rounded-md border bg-muted/20",
        isOver && column.droppable && "ring-2 ring-primary/50 bg-primary/5",
      )}
    >
      <div className="px-2.5 py-2 border-b border-border/50">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {column.label}
          </h3>
          <span className="text-[11px] text-muted-foreground">{offers.length}</span>
        </div>
        <div className="mt-1 text-xs tabular-nums">
          <span className="font-medium">{formatNOK(rawKr)}</span>
          {weightedKr !== rawKr && (
            <span className="text-muted-foreground"> · vektet {formatNOK(weightedKr)}</span>
          )}
        </div>
      </div>
      <div className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[80px]">{children}</div>
    </div>
  );
}

interface Props {
  /** Allerede filtrert på søk/"Mine tilbud" — boardet grupperer selv per status. */
  offers: OfferWithCustomer[];
}

/**
 * VIB-1304: kanban-board for tilbud. Kolonner draft/sent alltid synlig;
 * accepted/lost/expired bak "Vis avsluttede"-toggle. Drag-and-drop kaller
 * SAMME kodesti som editorens statusendring (useOfferStatusChange). Drop på
 * Tapt holdes tilbake til LostReasonDialog er bekreftet — avbryt = ingen
 * statusendring (kortet blir liggende siden offer.status er uendret).
 * Rendres KUN når feature_offer_pipeline er på (gates i OffersPage).
 */
export function OfferPipelineBoard({ offers }: Props) {
  const [showClosed, setShowClosed] = useState(false);
  const [sellerFilter, setSellerFilter] = useState<string>("all");
  const [pendingLostOfferId, setPendingLostOfferId] = useState<string | null>(null);
  // VIB-2094: drar man et utkast til Sendt uten sannsynlighet/close-dato,
  // holdes flyttingen tilbake til SendOfferDialog er bekreftet — samme
  // mekanikk som Tapt over. Avbryt = kortet blir liggende.
  const [pendingSendOfferId, setPendingSendOfferId] = useState<string | null>(null);
  const { data: weights } = useOfferPipelineWeights();
  const { changeStatus, isPending } = useOfferStatusChange(true);
  const today = useMemo(() => new Date(), []);

  const sellers = useMemo(() => {
    const byId = new Map<string, string>();
    for (const o of offers) {
      if (o.creator) byId.set(o.creator.id, o.creator.full_name);
    }
    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1], "nb"));
  }, [offers]);

  const filtered = useMemo(
    () => (sellerFilter === "all" ? offers : offers.filter((o) => o.created_by === sellerFilter)),
    [offers, sellerFilter],
  );

  const groups = useMemo(() => {
    const byBucket: Record<PipelineBucket, OfferWithCustomer[]> = {
      draft: [],
      sent: [],
      expired: [],
      accepted: [],
      lost: [],
    };
    for (const o of filtered) {
      byBucket[bucketFor(o, today)].push(o);
    }
    return byBucket;
  }, [filtered, today]);

  const columnTotals = useMemo(() => {
    const totals: Record<PipelineBucket, { rawKr: number; weightedKr: number }> = {
      draft: { rawKr: 0, weightedKr: 0 },
      sent: { rawKr: 0, weightedKr: 0 },
      expired: { rawKr: 0, weightedKr: 0 },
      accepted: { rawKr: 0, weightedKr: 0 },
      lost: { rawKr: 0, weightedKr: 0 },
    };
    if (!weights) return totals;
    for (const bucket of Object.keys(groups) as PipelineBucket[]) {
      const pipelineOffers = groups[bucket].map(toPipelineOffer);
      // weightedPipeline filtrerer selv på status='sent'+ikke-utløpt — for
      // andre kolonner (draft/accepted/lost/expired) bruker vi samme
      // vektefunksjon med `today` justert så filteret alltid slipper
      // gjennom radene som allerede er forhåndsgruppert her.
      let rawKr = 0;
      let weightedKr = 0;
      for (const o of pipelineOffers) {
        rawKr += o.total_amount;
        if (o.probability) weightedKr += o.total_amount * weights[o.probability];
      }
      totals[bucket] = { rawKr: Math.round(rawKr), weightedKr: Math.round(weightedKr) };
    }
    return totals;
  }, [groups, weights]);

  function handleDragEnd(event: DragEndEvent) {
    const offerId = String(event.active.id);
    const targetBucket = event.over?.id as PipelineBucket | undefined;
    if (!targetBucket) return;
    const offer = filtered.find((o) => o.id === offerId);
    if (!offer) return;
    const currentBucket = bucketFor(offer, today);
    if (currentBucket === targetBucket) return;

    if (targetBucket === "lost") {
      setPendingLostOfferId(offerId);
      return;
    }
    if (targetBucket === "expired") return; // ikke et gyldig drop-mål
    if (targetBucket === "sent" && missingBeforeSend(offer)) {
      setPendingSendOfferId(offerId);
      return;
    }
    changeStatus(offer, targetBucket as OfferStatus);
  }

  const pendingLostOffer = filtered.find((o) => o.id === pendingLostOfferId) ?? null;
  const pendingSendOffer = filtered.find((o) => o.id === pendingSendOfferId) ?? null;
  const columns = showClosed ? [...CORE_COLUMNS, ...CLOSED_COLUMNS] : CORE_COLUMNS;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          variant={showClosed ? "default" : "outline"}
          size="sm"
          onClick={() => setShowClosed((v) => !v)}
        >
          Vis avsluttede
        </Button>
        <Select value={sellerFilter} onValueChange={setSellerFilter}>
          <SelectTrigger className="h-9 w-[180px] text-sm">
            <SelectValue placeholder="Alle selgere" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle selgere</SelectItem>
            {sellers.map(([userId, name]) => (
              <SelectItem key={userId} value={userId}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* dnd-kit sin KeyboardSensor er aktiv, men usynlig uten hint (VIB-2543). */}
        <p className="text-xs text-muted-foreground">
          Tastatur: Tab til et kort, mellomrom, piler, mellomrom for å flytte.
        </p>
      </div>

      <DndContext onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {columns.map((column) => (
            <DroppableColumn
              key={column.bucket}
              column={column}
              offers={groups[column.bucket]}
              rawKr={columnTotals[column.bucket].rawKr}
              weightedKr={columnTotals[column.bucket].weightedKr}
            >
              {groups[column.bucket].length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">Ingen tilbud</p>
              ) : (
                groups[column.bucket].map((o) => (
                  <OfferBoardCard key={o.id} offer={o} disabled={column.bucket === "expired" || isPending} />
                ))
              )}
            </DroppableColumn>
          ))}
        </div>
      </DndContext>

      <LostReasonDialog
        open={!!pendingLostOffer}
        onOpenChange={(open) => {
          if (!open) setPendingLostOfferId(null);
        }}
        offerTitle={pendingLostOffer?.title ?? ""}
        isSubmitting={isPending}
        onConfirm={(reason) => {
          if (pendingLostOffer) changeStatus(pendingLostOffer, "lost", { lostReason: reason });
          setPendingLostOfferId(null);
        }}
      />

      <SendOfferDialog
        open={!!pendingSendOffer}
        onOpenChange={(open) => {
          if (!open) setPendingSendOfferId(null);
        }}
        offerTitle={pendingSendOffer?.title ?? ""}
        currentProbability={pendingSendOffer?.probability ?? null}
        currentExpectedCloseDate={pendingSendOffer?.expected_close_date ?? null}
        isSubmitting={isPending}
        onConfirm={(fields) => {
          if (pendingSendOffer) {
            changeStatus(pendingSendOffer, "sent", { sendFields: fields });
          }
          setPendingSendOfferId(null);
        }}
      />
    </div>
  );
}
